import { FaPhoneAlt, FaEnvelope, FaUser, FaTools, FaMapMarkerAlt, FaClock } from "react-icons/fa";
import { useRef, useEffect, useState } from "react";

// Intersection Observer for reveal-on-visible animation
function useOnScreen(ref, rootMargin = "0px") {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isIntersecting;
}

function ContactHero() {
  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const isVisible = useOnScreen(sectionRef, "-60px");

  useEffect(() => {
    if (isVisible && !hasAnimated) setHasAnimated(true);
  }, [isVisible, hasAnimated]);

  // Animation styles
  const animStyle = (delay = 0, y = 32, duration = 1100) => ({
    opacity: hasAnimated ? 1 : 0,
    transform: hasAnimated ? "translateY(0px)" : `translateY(${y}px)`,
    transition: `all ${duration}ms cubic-bezier(0.33,0.75,0.68,1)`,
    transitionDelay: hasAnimated ? `${delay}ms` : "0ms",
    willChange: "opacity, transform",
  });

  return (
    <section
      ref={sectionRef}
      className="relative pb-24 pt-40 font-PTSerif-Regular"
      style={{
        backgroundImage:
          "url('https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1500&q=80')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-blue-800/80"></div>

      {/* Content */}
      <div
        className="relative max-w-7xl mx-auto px-6 text-center text-white z-10 font-PTSerif-Regular"
        style={animStyle(0, 34, 900)}
      >
        <h1
          className="text-4xl font-bold mb-6 font-PTSerif-Regular"
          style={animStyle(120, 16, 800)}
        >
          Contact STRALIA PRIVATE LIMITED
        </h1>

        <p
          className="max-w-2xl mx-auto text-blue-100 leading-relaxed mb-10 font-PTSerif-Regular"
          style={animStyle(220, 10, 700)}
        >
          Need professional IT support, networking services, or CCTV installation?
          Our experienced engineers are ready to assist your business with
          reliable technical solutions and fast response.
        </p>

        <div
          className="flex flex-col md:flex-row justify-center gap-8 font-PTSerif-Regular"
          style={animStyle(320, 26, 700)}
        >
          <div className="flex items-center gap-3 justify-center font-PTSerif-Regular">
            <FaPhoneAlt />
            <span>9217208800 / 9217209900 / 9217204400</span>
          </div>
          <div className="flex items-center gap-3 justify-center font-PTSerif-Regular">
            <FaEnvelope />
            <span>straliapvtltd@gmail.com</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactInfo() {
  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const isVisible = useOnScreen(sectionRef, "-60px");

  useEffect(() => {
    if (isVisible && !hasAnimated) setHasAnimated(true);
  }, [isVisible, hasAnimated]);

  const animStyle = (delay = 0, y = 32, duration = 1100) => ({
    opacity: hasAnimated ? 1 : 0,
    transform: hasAnimated ? "translateY(0px)" : `translateY(${y}px)`,
    transition: `all ${duration}ms cubic-bezier(0.33,0.75,0.68,1)`,
    transitionDelay: hasAnimated ? `${delay}ms` : "0ms",
    willChange: "opacity, transform",
  });

  return (
    <section ref={sectionRef} className="py-24 bg-gray-100 font-PTSerif-Regular">
      <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 font-PTSerif-Regular">
        {/* Contact Details */}
        <div style={animStyle(0, 30, 900)}>
          <h2 className="text-3xl font-bold mb-8 font-PTSerif-Regular">
            Get In Touch
          </h2>
          <div className="space-y-8 font-PTSerif-Regular">
            <div className="flex gap-4 font-PTSerif-Regular">
              <FaMapMarkerAlt className="text-blue-600 text-xl mt-1" />
              <p className="text-gray-600 font-PTSerif-Regular">
                Office No. 179-A First Floor (B/P),
                Jivan Nagar, Ashram, South East Delhi - 110014
              </p>
            </div>
            <div className="flex gap-4 font-PTSerif-Regular">
              <FaPhoneAlt className="text-blue-600 text-xl mt-1" />
              <div className="text-gray-600 font-PTSerif-Regular">
                <p>9217208800</p>
                <p>9217209900</p>
                <p>9217204400</p>
              </div>
            </div>
            <div className="flex gap-4 font-PTSerif-Regular">
              <FaClock className="text-blue-600 text-xl mt-1" />
              <p className="text-gray-600 font-PTSerif-Regular">
                Monday – Saturday <br />
                9:30 AM – 7:00 PM
              </p>
            </div>
          </div>
        </div>

        {/* Map */}
        <div
          className="rounded-lg overflow-hidden shadow-lg font-PTSerif-Regular"
          style={animStyle(180, 40, 1200)}
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3503.749999003449!2d77.25960207549873!3d28.57726897569504!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjjCsDM0JzM4LjIiTiA3N8KwMTUnNDMuOCJF!5e0!3m2!1sen!2sin!4v1773214932954!5m2!1sen!2sin"
            width="600"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Google Map Location"
            className="w-full h-[400px] border-0 font-PTSerif-Regular"
          ></iframe>
        </div>
      </div>
    </section>
  );
}

function ContactForm() {
  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);
  const isVisible = useOnScreen(sectionRef, "-60px");

  // Form state
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    if (isVisible && !hasAnimated) setHasAnimated(true);
  }, [isVisible, hasAnimated]);

  const animStyle = (delay = 0, y = 32, duration = 1100) => ({
    opacity: hasAnimated ? 1 : 0,
    transform: hasAnimated ? "translateY(0px)" : `translateY(${y}px)`,
    transition: `all ${duration}ms cubic-bezier(0.33,0.75,0.68,1)`,
    transitionDelay: hasAnimated ? `${delay}ms` : "0ms",
    willChange: "opacity, transform",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // API call integration
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset previous messages
    setSubmitError(null);
    setSubmitSuccess(null);

    // Basic frontend validation
    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.phone.trim() ||
      !form.service.trim() ||
      !form.message.trim()
    ) {
      setSubmitError("Please fill in all the fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      // API endpoint conforms to server from @file_context_0
      const apiUrl = process.env.REACT_APP_API_URL
        ? `${process.env.REACT_APP_API_URL}/send-mail`
        : "/api/contact"; // fallback for dev

      // The backend expects keys: name, email, phone, message, service, and selectedBrand (skip selectedBrand for IT main)
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service,
        message: form.message,
        brand:'stralia'
        // selectedBrand can be omitted for IT
      };
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.text();

      if (!response.ok) {
        throw new Error(data || "Something went wrong");
      }

      setSubmitSuccess(
        "Thank you! Your support request has been received. Our team will contact you shortly."
      );
      setForm({
        name: "",
        email: "",
        phone: "",
        service: "",
        message: "",
      });
    } catch (err) {
      setSubmitError(
        err.message ||
          "Failed to send your request. Please try again or contact us by phone/email."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={sectionRef} className="py-24 bg-white font-PTSerif-Regular">
      <div className="max-w-4xl mx-auto px-6 font-PTSerif-Regular">
        <div
          className="text-center mb-16 font-PTSerif-Regular"
          style={animStyle(0, 30, 800)}
        >
          <h2
            className="text-3xl font-bold font-PTSerif-Regular"
            style={animStyle(120, 18, 700)}
          >
            Request IT Support
          </h2>
          <p className="text-gray-600 mt-4 font-PTSerif-Regular" style={animStyle(200, 8, 700)}>
            Fill out the form and our technical team will contact you shortly.
          </p>
        </div>
        <form
          className="grid md:grid-cols-2 gap-6 font-PTSerif-Regular"
          style={animStyle(300, 32, 950)}
          onSubmit={handleSubmit}
        >
          {/* Name */}
          <div className="relative font-PTSerif-Regular" style={animStyle(0, 14, 600)}>
            <FaUser className="absolute left-4 top-4 text-gray-400" />
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Your Name"
              className="w-full pl-12 pr-4 py-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 font-PTSerif-Regular"
              disabled={isSubmitting}
              required
            />
          </div>
          {/* Email */}
          <div className="relative font-PTSerif-Regular" style={animStyle(80, 12, 600)}>
            <FaEnvelope className="absolute left-4 top-4 text-gray-400" />
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Email Address"
              className="w-full pl-12 pr-4 py-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 font-PTSerif-Regular"
              disabled={isSubmitting}
              required
            />
          </div>
          {/* Phone */}
          <div className="relative font-PTSerif-Regular" style={animStyle(160, 12, 600)}>
            <FaPhoneAlt className="absolute left-4 top-4 text-gray-400" />
            <input
              type="text"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              placeholder="Phone Number"
              className="w-full pl-12 pr-4 py-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 font-PTSerif-Regular"
              disabled={isSubmitting}
              required
            />
          </div>
          {/* Service */}
          <div className="relative font-PTSerif-Regular" style={animStyle(240, 12, 600)}>
            <FaTools className="absolute left-4 top-4 text-gray-400" />
            <select
              name="service"
              value={form.service}
              onChange={handleChange}
              className="w-full pl-12 pr-4 py-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 font-PTSerif-Regular"
              disabled={isSubmitting}
              required
            >
              <option value="">Select Service</option>
              <option>Networking AMC</option>
              <option>Computer AMC</option>
              <option>Laptop AMC</option>
              <option>Server AMC</option>
              <option>Projector AMC</option>
              <option>CCTV AMC</option>
              <option>CCTV Installation</option>
              <option>All IT Services</option>
            </select>
          </div>
          {/* Message */}
          <textarea
            name="message"
            value={form.message}
            onChange={handleChange}
            placeholder="Describe your issue..."
            rows="5"
            className="md:col-span-2 border p-4 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 font-PTSerif-Regular"
            style={animStyle(320, 14, 700)}
            disabled={isSubmitting}
            required
          ></textarea>
          {/* Button */}
          <button
            type="submit"
            className="md:col-span-2 bg-blue-600 text-white py-3 rounded font-semibold hover:bg-blue-700 transition font-PTSerif-Regular"
            style={animStyle(400, 10, 700)}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending..." : "Send Request"}
          </button>
          {/* Success/Error message */}
          {submitSuccess && (
            <div className="md:col-span-2 text-green-700 bg-green-100 rounded p-3 text-center transition" style={animStyle(420, 0, 600)}>
              {submitSuccess}
            </div>
          )}
          {submitError && (
            <div className="md:col-span-2 text-red-700 bg-red-100 rounded p-3 text-center transition" style={animStyle(420, 0, 600)}>
              {submitError}
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

export default function ContactPage() {
  return (
    <>
      <ContactHero />
      <ContactInfo />
      <ContactForm />
    </>
  );
}